/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { isEqual, orderBy } from 'lodash-es';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ClientSettings } from '../models/client-settings';
import { Features } from '../enums/features';
import { HttpStatus } from '../enums/http-status';
import { ApiService } from './api/api.service';
import { AuthService } from './auth.service';
import { BaseAuthenticatedPollingService } from './base/base-authenticated-polling-service';
import { LoggingService } from './logging.service';
import { ErrorService } from './error.service';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class SettingsService extends BaseAuthenticatedPollingService {
  private readonly defaultSettings: ClientSettings = {
    templates: [],
    environment: {
      name: this.environmentService.configurationSettings.environmentName,
      version: this.environmentService.configurationSettings.releaseVersion,
    },
    placeholders: [
      '$(Caller.+TelNumber)',
      '$(Caller.TelNumber)',
      '$(Caller.DisplayName)',
      '$(Caller.Id)',
      '$(User.Id)',
      '$(User.Email)',
      '$(User.DisplayName)',
      '$(User.Upn)',
      '$(Random.Guid)',
    ],
    hasContactCenterLicense: false,
    notAvailableReasons: [],
    isDefault: true,
  };

  public settings$: BehaviorSubject<ClientSettings> = new BehaviorSubject<ClientSettings>(this.defaultSettings);
  public settingsUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
  public isLicensed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
  public hasContactCenterLicense$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
  public desktopVersion$ = new BehaviorSubject<string>(undefined);

  public get portalSessionsUrl(): string {
    const environment = this.environmentService.configurationSettings.mainLocation;
    return `https://portal.${environment}/personal/my-sessions`;
  }

  public get portalUrl(): string {
    const environment = this.environmentService.configurationSettings.mainLocation;
    return `https://portal.${environment}`;
  }

  public constructor(
    authService: AuthService,
    private readonly apiService: ApiService,
    private readonly environmentService: EnvironmentConfigurationService,
    private readonly loggingService: LoggingService,
    private readonly errorService: ErrorService,
    protected readonly configurationService: ConfigurationService,
  ) {
    super(authService, configurationService);
  }

  public async pollingAction(): Promise<void> {
    this.apiService
      .getSettings()
      .pipe(
        catchError((err) => this.onSettingsError(err)),
        filter((x) => !!x),
      )
      .subscribe((result: ClientSettings) => {
        this.handlePollingSuccess();
        this.checkPermissions(result);
        this.updateSettings(result);
      });
  }

  public async stopPollingAction(): Promise<void> {
    this.updateSettings(null);
  }

  public setDesktopVersion(desktopVersion: string): void {
    this.desktopVersion$.next(desktopVersion);
    this.loggingService.Info(`Desktop version: ${this.desktopVersion$.value}`);
  }

  public supportsFeature(feature: Features): boolean {
    // TODO DK: add semver and get version check back

    if (!new RegExp('^[0-9].[0-9]*.[0-9]*..*').test(this.desktopVersion$.value)) {
      return false;
    }

    const versionParts = this.desktopVersion$.value?.split('.');

    const major = Number(versionParts[0]);
    const minor = Number(versionParts[1]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-magic-numbers
    const patch = Number(versionParts[2]);

    switch (feature) {
      case Features.OpenLogFolder:
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return major > 1 || (major === 1 && minor >= 81);
      case Features.ZoomView:
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return major > 1 || (major === 1 && minor >= 86);
      case Features.AudioNotifications:
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return major > 1 || (major === 1 && minor >= 88);
      case Features.PreCallInfo:
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return major > 1 || (major === 1 && minor >= 102);
      default:
        return false;
    }
  }

  private onSettingsError(err: { httpStatus: number; error: string }): Observable<ClientSettings> {
    this.loggingService.Error(JSON.stringify(err));

    if (err.httpStatus === HttpStatus.FORBIDDEN_403 && err.error === 'Feature Assistant is disabled') {
      this.settingsUpdated$.next(true);
      return of({ ...this.defaultSettings, isDefault: false });
    }

    if (err.httpStatus === HttpStatus.NOT_FOUND_404) {
      this.settingsUpdated$.next(true);
      return of({ ...this.defaultSettings, isLicenseFailed: true, isDefault: false });
    }

    this.settingsUpdated$.next(false);
    this.handlePollingError();
    return of(null);
  }

  private updateSettings(result: ClientSettings): void {
    const settings = { ...this.defaultSettings, ...(result || {}), isDefault: !result || result.isDefault } as ClientSettings;

    if (!this.areEqual(this.settings$.value, settings)) {
      this.loggingService.Info(`NimbusSettings:\n${JSON.stringify(settings)}`);
    }
    this.settings$.next(settings);
    this.isLicensed$.next(result ? !settings.isLicenseFailed : undefined);
    this.hasContactCenterLicense$.next(result ? settings.hasContactCenterLicense : undefined);
    this.configurationService.updateConfigurationSettings(settings.configurationSettings);

    this.settingsUpdated$.next(!result ? undefined : !result.isDefault); // AFTER isLicensed$ and hasContactCenterLicense$
  }

  private areEqual(object1, object2): boolean {
    try {
      const keys1 = Object.keys(object1);
      const keys2 = Object.keys(object2);

      if (keys1.length !== keys2.length) {
        return false;
      }

      for (const key of keys1) {
        const val1 = object1[key];
        const val2 = object2[key];

        const areArrays = Array.isArray(val1) && Array.isArray(val2);
        if (areArrays) {
          if (!val1.length && !val2.length) {
            continue;
          }

          const isArrayOfObjects = this.isObject(val1[0]);
          const sorted1 = isArrayOfObjects ? orderBy(val1, ['id', 'name']) : orderBy(val1);
          const sorted2 = isArrayOfObjects ? orderBy(val2, ['id', 'name']) : orderBy(val2);
          if (!this.areEqual(sorted1, sorted2)) {
            return false;
          } else {
            continue;
          }
        }

        const areObjects = this.isObject(val1) && this.isObject(val2);
        if (areObjects) {
          if (!this.areEqual(val1, val2)) {
            return false;
          } else {
            continue;
          }
        }

        if (val1 !== val2) {
          return false;
        }
      }

      return true;
    } catch (ex) {
      return isEqual(object1, object2);
    }
  }

  private isObject(object): boolean {
    return object != null && typeof object === 'object';
  }

  private checkPermissions(settings: ClientSettings): void {
    if (settings.isTenantSuspended) {
      this.loggingService.Info(`Tenant suspended`);
      this.authService.logoutPopup().subscribe(() => {
        this.errorService.showError(TranslationKeys.AssistantTenantSuspended, TranslationKeys.AssistantContactLuwareSupport);
      });
    }
  }
}
